import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Experience.css';

const Experience = () => {
  const { language } = useLanguage();
  const isFrench = language === 'fr';

  const experiences = [
    {
      id: "junapay",
      title: "JunaPay",
      subtitle: "Plateforme de paiement mobile",
      period: "Depuis juin 2025",
      location: "Douala, Cameroun",
      client: "JunaPay Technologies",
      description: "Solution de paiement mobile haute sécurité pour l'Afrique francophone.",
      technologies: ["Java Spring Boot", "Flutter", "React", "Angular", "MySQL", "FCM"],
      achievements: [
        "Backend API REST avec Java Spring Boot",
        "Application mobile Flutter et dashboard Angular",
        "Notifications FCM et site vitrine React"
      ],
      hasDemo: true
    },
    {
      id: "thegift",
      title: "TheGift SAS",
      subtitle: "Co-fondatrice & CTO",
      period: "Depuis 2024",
      location: "Limoges, France",
      client: "TheGift SAS",
      description: "Startup et application mobile de recommandation de cadeaux — App Store & Google Play.",
      technologies: ["Kotlin Multiplatform", "Firebase", "React", "Firestore", "CI/CD", "Make"],
      achievements: [
        "Architecture Kotlin Multiplatform iOS / Android",
        "Algorithme de recommandation et back-office React",
        "Automatisations Make pour Firestore"
      ],
      hasDemo: true
    },
    {
      id: "noups-orca",
      title: "ORCA",
      subtitle: "Stage M1 — NOUPS Consulting",
      period: "Stage M1",
      location: "France",
      client: "NOUPS Consulting",
      description: "Outil d'automatisation de tests d'API en Angular et Java.",
      technologies: ["Angular", "Java", "REST API", "Tests automatisés"],
      achievements: [
        "Front Angular et backend Java",
        "Scénarios de tests d'API automatisés",
        "Rapport de stage"
      ],
      hasDemo: false
    },
    {
      id: "estlc-inscription",
      title: "Inscription Universitaire",
      subtitle: "Système ESTLC",
      period: "Mai – Août 2024",
      location: "Cameroun",
      client: "ESTLC",
      description: "Digitalisation du processus d'inscription pour 5000+ candidats annuels.",
      technologies: ["Spring Boot", "MySQL", "JavaMail API", "JWT"],
      achievements: [
        "Architecture microservices (−70 % temps de traitement)",
        "15+ endpoints REST sécurisés JWT",
        "Notifications automatiques (98 % délivrabilité)"
      ],
      hasDemo: false
    },
    {
      id: "genyshop",
      title: "GenyShop",
      subtitle: "E-commerce mobile",
      period: "Mars – Août 2023",
      location: "Douala, Cameroun",
      client: "ChickDev Douala",
      description: "Application e-commerce complète avec analyse fonctionnelle et technique.",
      technologies: ["Spring Boot", "Angular 15", "Flutter", "MySQL", "Docker"],
      achievements: [
        "Base SQL 15+ tables et APIs Swagger",
        "Backend microservices Spring Boot",
        "Application mobile Flutter"
      ],
      hasDemo: true
    }
  ];

  return (
    <section className="experience" id="experience">
      <div className="experience-container">
        <div className="section-intro">
          <h2 className="heading">
            {isFrench ? <>Expériences <span>professionnelles</span></> : <>Professional <span>Experience</span></>}
          </h2>
          <p className="section-subtitle">
            {isFrench
              ? 'Expériences professionnelles, missions et co-fondation de startup.'
              : 'Professional experiences, missions and startup co-founding.'}
          </p>
        </div>

        <div className="experience-list">
          {experiences.map((exp) => (
            <Link key={exp.id} to={`/experience/${exp.id}`} className="experience-item">
                <div className="experience-item-header">
                  <div className="experience-item-titles">
                    <span className="experience-item-subtitle">{exp.subtitle}</span>
                    <h3>{exp.title}</h3>
                  </div>
                  <div className="experience-item-badges">
                    <span className="experience-badge period">{exp.period}</span>
                    {exp.hasDemo && (
                      <span className="experience-badge live">
                        {isFrench ? 'En ligne' : 'Live'}
                      </span>
                    )}
                  </div>
                </div>

                <p className="experience-item-desc">{exp.description}</p>

                <ul className="experience-item-highlights">
                  {exp.achievements.map((item, idx) => (
                    <li key={idx}>
                      <CheckCircle2 size={16} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="experience-item-footer">
                  <div className="experience-item-meta">
                    <span><MapPin size={14} /> {exp.location}</span>
                    <span className="meta-sep">·</span>
                    <span>{exp.client}</span>
                  </div>
                  <div className="experience-item-tech">
                    {exp.technologies.slice(0, 5).map((tech) => (
                      <span key={tech} className="tech-pill">{tech}</span>
                    ))}
                  </div>
                  <span className="experience-item-link">
                    {isFrench ? 'Voir le détail' : 'View details'}
                    <ArrowRight size={16} />
                  </span>
                </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
